import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import { useContext, useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AppConstantsContext, UserContext } from '../../../../context';
import type { InstrumentType } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { TranscriptionDialogContext } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType } from '../../../../entities';
import {
  backendService,
  TrackingCategory,
  TrackingTranscriptionParams,
  trackingService,
} from '../../../../services';
import type { Step } from './TranscriptionDialog.config';
import {
  HELPER_TEXT, STEP_TITLE, STEPS, useSteps,
} from './TranscriptionDialog.config';
import TranscriptionDialogWrapper from './TranscriptionDialogWrapper';
import AdvancedSettings from './steps/AdvancedSettings';
import Capo from './steps/Capo';
import ComposerTitle from './steps/ComposerTitle';
import DrumSet from './steps/DrumSet';
import GuitarStyle from './steps/GuitarStyle';
import InstrumentsSelection from './steps/InstrumentsSelection';
import PedalMarkings from './steps/PedalMarkings';
import PianoStyle from './steps/PianoStyle';
import SoloComponent from './steps/SoloComponent';
import Tuning from './steps/Tuning';
import Lyrics from './steps/Lyrics';
import SpecificInstrument from './steps/SpecificInstrument';

export interface TranscriptionDialogProps {
  open: boolean;
  handleClose: () => void;
  handleProcessing?: (processing: number) => void;
  handleResolve?: (jobId: string, useTickets: boolean) => void;
  customSubmitButton?: React.ReactNode;
  isRetranscribe?: boolean;
  location?: (typeof TrackingTranscriptionParams.location)['songbook' | 'wp' | 'nav'];
}
const TranscriptionDialog: React.FC<TranscriptionDialogProps> = ({
  open,
  handleClose,
  handleProcessing,
  handleResolve,
  customSubmitButton,
  isRetranscribe,
  location,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>(STEPS.CHOOSE_INSTRUMENT);
  const { stepList, setStepList } = useSteps();
  const { jobType } = useContext(AppConstantsContext);
  const { settings, apriori, youTubeUrl, file, reset, source } = useContext(
    TranscriptionDialogContext,
  );

  const { user } = useContext(UserContext);
  const [rerender, setRerender] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (rerender) {
      setCurrentStep(stepList[stepList.indexOf(currentStep) + 1]);
      setRerender(false);
    }
  }, [rerender]);

  useEffect(() => {
    if (isRetranscribe) {
      setStepList(settings.instruments, settings.solo, isRetranscribe);
    }
  }, [isRetranscribe, settings]);

  const setStep = () => {
    const nextStepIndex = stepList.indexOf(currentStep) + 1;
    if (nextStepIndex < stepList.length) {
      setCurrentStep(stepList[nextStepIndex]);
    }
  };

  const setSoloStep = (instruments: InstrumentType[]) => {
    if (instruments.length === 1) {
      setCurrentStep(STEPS.SOLO);
    } else {
      setStepList(instruments, false, isRetranscribe);
      setRerender(true);
    }
  };

  const setMandatorySteps = (soloSelected: boolean) => {
    if (soloSelected) {
      setStepList(settings.instruments, true, isRetranscribe);
      setRerender(true);
    } else {
      setStepList(settings.instruments, false, isRetranscribe);
      setRerender(true);
    }
  };

  const onSubmit = (useTickets: boolean) => {
    setSubmitting(true);
    const trackingValues = {
      type: useTickets
        ? TrackingTranscriptionParams.type.full
        : TrackingTranscriptionParams.type.free,
      location,
      input: youTubeUrl ? TrackingTranscriptionParams.input.youtube : source,
    };
    trackingService.valueObjectEvent<{ type: string; location: string; input: string }>(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.create,
      { ...trackingValues },
    );
    // console.log(TrackingTranscriptionParams.action.create, { ...trackingValues });
    const params = {
      settings,
      apriori,
    };
    if (youTubeUrl && !file) {
      return TranscriptionService.transcriptionYouTube({
        youTubeUrl,
        useCredits: useTickets,
        params: JSON.stringify(params),
        type: jobType,
      }).then((res) => {
        handleResolve(res?.jobId as string, useTickets);
      });
    }

    return backendService.transcribeFile(
      file,
      useTickets,
      params,
      source,
      jobType,
      user?.token,
      (perc) => {
        handleProcessing(perc);
      },
      (jobId) => {
        handleResolve(jobId.toString(), useTickets);
      },
    );
  };

  const onClickBack = () => {
    setCurrentStep(stepList[stepList.indexOf(currentStep) - 1]);
  };

  const handleCloseDialog = () => {
    reset();
    setCurrentStep(null);
    setStepList([], false, false);
    setSubmitting(false);
    trackingService.event(TrackingCategory.Transcription, 'action_wizard_close');
    handleClose();
  };
  const helpText = jobType === JobType.TranscribeStudio && currentStep === STEPS.CHOOSE_INSTRUMENT
    ? t('home.input.dialog.choose_instrument.help_multi')
    : t(HELPER_TEXT[currentStep]);

  const title = jobType === JobType.TranscribeStudio && currentStep === STEPS.CHOOSE_INSTRUMENT
    ? t('home.input.dialog.choose_instrument.title_multi')
    : t(STEP_TITLE[currentStep]);

  return (
    <TranscriptionDialogWrapper
      submitting={submitting}
      open={open}
      handleClose={handleCloseDialog}
      handleBackClick={onClickBack}
      currentStep={stepList.indexOf(currentStep)}
      numberOfSteps={stepList.length}
      title={title}
      helperText={helpText}
    >
      {currentStep === STEPS.CHOOSE_INSTRUMENT && (
        <InstrumentsSelection
          isRetranscribe={isRetranscribe}
          handleMultipleSelection={(isMultiple) => setSoloStep(isMultiple)}
        />
      )}
      {currentStep === STEPS.SOLO && <SoloComponent setCurrentStep={setMandatorySteps} />}
      {currentStep === STEPS.SPECIFY_INSTRUMENT && <SpecificInstrument setCurrentStep={setStep} />}

      {currentStep === STEPS.PEDAL && <PedalMarkings setCurrentStep={setStep} />}
      {currentStep === STEPS.PIANO_STYLE && <PianoStyle setCurrentStep={setStep} />}
      {currentStep === STEPS.GUITAR_STYLE && <GuitarStyle setCurrentStep={setStep} />}

      {currentStep === STEPS.TUNING && <Tuning setCurrentStep={setStep} />}
      {currentStep === STEPS.CAPO && <Capo setCurrentStep={setStep} />}

      {currentStep === STEPS.LYRICS && <Lyrics setCurrentStep={setStep} />}

      {currentStep === STEPS.DRUMSET && <DrumSet setCurrentStep={setStep} />}

      {currentStep === STEPS.ADVANCED && (
        <AdvancedSettings isRetranscribe={isRetranscribe} setCurrentStep={setStep} />
      )}

      {currentStep === STEPS.COMPOSER_TITLE && <ComposerTitle onSubmit={onSubmit} />}
      {stepList[stepList.indexOf(currentStep)] === stepList[stepList.length - 1]
        && customSubmitButton}
    </TranscriptionDialogWrapper>
  );
};

export default TranscriptionDialog;
